import type { Editor as EditorType } from 'hugerte';

const OLD_SHORTCUT_PATTERN = /alt\s*[+-]\s*0/i;
const NEW_SHORTCUT_TEXT = 'Control+Option+0 on Mac, or Shift+Alt+0 on Windows';

/**
 * hugerte's content-iframe body gets an aria-label translated from
 * "Rich Text Area. Press ALT-0 for help." in every locale (config/hugerte/langs).
 * Our hugerte patch rebinds that shortcut to Access+0 (see patches/hugerte.patch),
 * so the baked-in translation is stale. Rather than hardcode a fixed English
 * replacement (losing the per-locale translation) or edit all 58 vendored locale
 * files (which would need redoing on every hugerte update), take whatever
 * translation is already loaded and swap out just the shortcut token.
 *
 * Spelled out in words rather than the ⌃⌥ glyphs hugerte's own toolbar tooltip
 * uses: this string is read by screen readers, not sighted users pattern-matching
 * a physical keyboard, and there's no guarantee those glyphs are pronounced
 * sensibly (or at all) across VoiceOver/NVDA/JAWS/TalkBack.
 *
 * This calls hugerte's own `editor.translate`, not Corgi's i18n `translate` —
 * the lint rule below can't tell the two apart from the method name alone.
 */
export const fixHelpAriaText = (editor: EditorType): void => {
	// eslint-disable-next-line @change-corgi/no-invalid-translation-keys -- hugerte's own translate, not Corgi's i18n
	const translated = editor.translate('Rich Text Area. Press ALT-0 for help.');
	if (!OLD_SHORTCUT_PATTERN.test(translated)) return;

	const corrected = translated.replace(OLD_SHORTCUT_PATTERN, NEW_SHORTCUT_TEXT);
	editor.getBody().setAttribute('aria-label', corrected);
};
